/**
 * LiveKit 媒体层
 *
 * 负责房间连接、本地轨道发布、远端轨道挂载与资源释放。
 * 业务组件只与本模块交互，不直接持有 livekit-client 的 Room 实例。
 * livekit-client 体积较大，按需动态加载，不进入首屏包。
 */

let room = null
let sdkModule = null

const CONNECT_TIMEOUT_MS = 15000

async function loadSdk() {
	if (!sdkModule) {
		sdkModule = await import('livekit-client')
	}
	return sdkModule
}

/**
 * LiveKit 服务地址可能是 https://（与配置项一致），SDK 需要 ws(s)://
 * 统一在此转换，调用方不感知
 */
function normalizeUrl(url) {
	if (!url) {
		return ''
	}
	if (url.indexOf('https://') === 0) {
		return 'wss://' + url.slice(8)
	}
	if (url.indexOf('http://') === 0) {
		return 'ws://' + url.slice(7)
	}
	return url
}

function withTimeout(promise, timeoutMs, message) {
	let timer = null
	const timeout = new Promise((resolve, reject) => {
		timer = setTimeout(() => reject(new Error(message)), timeoutMs)
	})
	return Promise.race([promise, timeout]).finally(() => {
		if (timer) {
			clearTimeout(timer)
		}
	})
}

/**
 * 连接房间并发布本地轨道
 * @param {Object} options
 * @param {string} options.url LiveKit 服务地址
 * @param {string} options.token 房间令牌
 * @param {string} options.mode voice|video
 * @param {Function} options.onRemoteTrack (track, participant) 远端轨道可用
 * @param {Function} options.onRemoteTrackRemoved (track, participant) 远端轨道移除
 * @param {Function} options.onParticipantDisconnected 对端离开房间
 * @param {Function} options.onDisconnected (reason) 本端断开
 * @param {Function} options.onReconnecting 网络抖动重连中
 * @param {Function} options.onReconnected 重连成功
 */
async function connect(options) {
	const {
		url,
		token,
		mode,
		onRemoteTrack,
		onRemoteTrackRemoved,
		onParticipantDisconnected,
		onParticipantConnected,
		onDisconnected,
		onReconnecting,
		onReconnected
	} = options
	if (!url || !token) {
		throw new Error('通话服务地址或房间令牌缺失')
	}

	await disconnect()

	const { Room, RoomEvent, VideoPresets } = await loadSdk()
	const currentRoom = new Room({
		adaptiveStream: true,
		dynacast: true,
		simulcast: true,
		videoCaptureDefaults: {
			resolution: VideoPresets.h720.resolution
		}
	})
	room = currentRoom

	currentRoom.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
		onRemoteTrack && onRemoteTrack(track, participant)
	})
	currentRoom.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
		onRemoteTrackRemoved && onRemoteTrackRemoved(track, participant)
	})
	currentRoom.on(RoomEvent.ParticipantConnected, (participant) => {
		onParticipantConnected && onParticipantConnected(participant)
	})
	currentRoom.on(RoomEvent.ParticipantDisconnected, (participant) => {
		onParticipantDisconnected && onParticipantDisconnected(participant)
	})
	currentRoom.on(RoomEvent.Disconnected, (reason) => {
		onDisconnected && onDisconnected(reason)
	})
	currentRoom.on(RoomEvent.Reconnecting, () => {
		onReconnecting && onReconnecting()
	})
	currentRoom.on(RoomEvent.Reconnected, () => {
		onReconnected && onReconnected()
	})

	await withTimeout(
		currentRoom.connect(normalizeUrl(url), token),
		CONNECT_TIMEOUT_MS,
		'连接通话服务超时'
	)

	// 音频必须先发布；视频发布失败由调用方降级为语音
	await currentRoom.localParticipant.setMicrophoneEnabled(true)
	let cameraEnabled = false
	if (mode === 'video') {
		try {
			await currentRoom.localParticipant.setCameraEnabled(true)
			cameraEnabled = true
		} catch (e) {
			// 摄像头被占用或无权限：保留音频继续通话
			cameraEnabled = false
		}
	}

	return { cameraEnabled }
}

/**
 * 交换机麦克风发布状态，返回切换后的状态
 */
async function setMicrophoneEnabled(enabled) {
	if (!room) {
		return false
	}
	await room.localParticipant.setMicrophoneEnabled(enabled)
	return room.localParticipant.isMicrophoneEnabled
}

/**
 * 切换摄像头发布状态，返回切换后的状态
 */
async function setCameraEnabled(enabled) {
	if (!room) {
		return false
	}
	await room.localParticipant.setCameraEnabled(enabled)
	return room.localParticipant.isCameraEnabled
}

/**
 * 解除轨道挂载并释放媒体元素
 */
function detachTrack(track) {
	if (!track) {
		return
	}
	try {
		track.detach()
	} catch (e) {
		// 轨道已随房间释放，忽略
	}
}

/**
 * 把本地摄像头轨道挂载到预览元素
 */
function attachLocalVideo(element) {
	if (!room || !element) {
		return
	}
	room.localParticipant.videoTrackPublications.forEach((publication) => {
		if (publication.track) {
			publication.track.attach(element)
		}
	})
}

/**
 * 断开并释放资源；重复调用安全
 */
async function disconnect() {
	if (!room) {
		return
	}
	const currentRoom = room
	room = null
	try {
		// 先停止采集，避免摄像头指示灯残留
		await currentRoom.localParticipant.setCameraEnabled(false)
	} catch (e) {
		// 摄像头本就未发布，忽略
	}
	try {
		await currentRoom.localParticipant.setMicrophoneEnabled(false)
	} catch (e) {
		// 麦克风本就未发布，忽略
	}
	try {
		await currentRoom.disconnect()
	} catch (e) {
		// 连接已断开，忽略
	}
}

export {
	attachLocalVideo,
	connect,
	detachTrack,
	disconnect,
	setCameraEnabled,
	setMicrophoneEnabled
}
